import './polyfills';

import React from 'react';
import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import { ThemeProvider } from './design-system';
import { ProjectsProvider } from './contexts/projectsContext';
import { DeadlinesProvider } from './contexts/deadlinesContext';
import { OrgSearchProvider } from './contexts/orgsearchContext';
import { SessionProvider } from './contexts/sessionContext';
import { Theme, GlobalStyle } from './theme';
import App from './App';
import config from './config';
import ErrorBoundary from './components/ErrorBoundary';

// Configure axios to send credentials (cookies) with all requests
axios.defaults.withCredentials = true;

// Laravel handles CSRF automatically via encrypted XSRF-TOKEN cookie

const PLAN_LIMIT_MESSAGE = 'You have exceeded your limit';

if (config.APP_ENV !== 'local') {
  console.log('Sentry enabled');
  Sentry.init({
    dsn: config.UI_SENTRY_DSN,
    environment: config.APP_ENV,
    tracesSampleRate: 1.0,
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      'ResizeObserver loop limit exceeded',
    ],
  });
}

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status as number | undefined;
    const bodyCode = error?.response?.data?.code as number | undefined;
    const bodyMessage: string =
      error?.response?.data?.message || error?.response?.data?.error || '';
    const isAccessDenied = bodyCode === 403;
    const isPlanLimit =
      bodyCode === 405 || bodyMessage.includes(PLAN_LIMIT_MESSAGE);
    if (
      !isAccessDenied &&
      !isPlanLimit &&
      status !== 404 &&
      status !== 403 &&
      ![400, 401, 419, 422].includes(status as number)
    ) {
      Sentry.captureException(error);
    }
    return Promise.reject(error);
  },
);

// styled-components v6 forwards every prop to the DOM by default; restore the
// v5 behavior of filtering non-HTML props (isOpen, route, …) app-wide.
const forwardValidProps = (prop: string, target: unknown) =>
  typeof target === 'string' ? isPropValid(prop) : true;

const tree = (
  <Sentry.ErrorBoundary fallback={ErrorBoundary}>
    <SessionProvider>
      <ProjectsProvider>
        <DeadlinesProvider>
          <OrgSearchProvider>
            <StyleSheetManager shouldForwardProp={forwardValidProps}>
              <ThemeProvider theme={Theme}>
                <GlobalStyle />
                <App />
              </ThemeProvider>
            </StyleSheetManager>
          </OrgSearchProvider>
        </DeadlinesProvider>
      </ProjectsProvider>
    </SessionProvider>
  </Sentry.ErrorBoundary>
);

const root = createRoot(document.getElementById('root')!);
root.render(
  config.APP_ENV === 'local' ? (
    <React.StrictMode>{tree}</React.StrictMode>
  ) : (
    tree
  ),
);
